import { SeasonSelect } from "@/components/ui/season-select";
import React from "react";
import { Download, Printer, RefreshCw, Search, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/components/ui/use-toast";
import { fetchCustomerRepresentatives, fetchCustomersSepaStatus } from "@/lib/api/customers";
import { fetchTemporadas } from "@/lib/api/products";

const sepaOptions = [
  { value: "all", label: "Todos" },
  { value: "with", label: "Con SEPA" },
  { value: "without", label: "Sin SEPA" },
];

const currencyFormatter = new Intl.NumberFormat("es-ES", {
  style: "currency",
  currency: "EUR",
});

function formatCurrency(value) {
  const number = Number(value);
  return currencyFormatter.format(Number.isFinite(number) ? number : 0);
}

function escapeHtml(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function csvCell(value) {
  const text = String(value ?? "");
  if (/[";\n\r]/.test(text)) {
    return `"${text.replace(/"/g, '""')}"`;
  }
  return text;
}

function filePart(value) {
  return String(value ?? "")
    .trim()
    .replace(/[^a-z0-9_-]+/gi, "-")
    .replace(/^-+|-+$/g, "")
    .toLowerCase();
}

function buildCsv(rows, { showOrderTotals = false, showRepresentativeName = false } = {}) {
  const header = showRepresentativeName
    ? ["Nombre", "Representante", "CIF", "Codigo", "Telefono", "Email"]
    : ["#", "Nombre", "CIF", "Codigo", "Telefono", "Email"];
  if (showOrderTotals) header.push("Importe pedidos");
  header.push("SEPA");

  const lines = rows.map((row, index) => {
    const values = showRepresentativeName
      ? [row.name, row.representativeName, row.cif, row.code, row.phone, row.email]
      : [index + 1, row.name, row.cif, row.code, row.phone, row.email];
    if (showOrderTotals) values.push(formatCurrency(row.orderTotal));
    values.push(row.hasSepa ? "OK" : "KO");
    return values.map(csvCell).join(";");
  });
  // BOM para que Excel reconozca UTF-8 correctamente.
  return `\ufeff${[header.join(";"), ...lines].join("\r\n")}`;
}

function buildPrintHtml(rows, {
  sepaFilterLabel,
  orderFilterLabel,
  representativeFilterLabel,
  showOrderTotals,
  showRepresentativeName,
}) {
  const generatedAt = new Date().toLocaleString("es-ES");
  const withSepa = rows.filter((row) => row.hasSepa).length;
  const withoutSepa = rows.length - withSepa;
  const accumulatedOrderTotal = rows.reduce((sum, row) => sum + (Number(row.orderTotal) || 0), 0);
  const showCounterColumn = !showRepresentativeName;
  const columnCount =
    (showCounterColumn ? 1 : 0) + 5 + (showRepresentativeName ? 1 : 0) + (showOrderTotals ? 1 : 0) + 1;
  const counterHeader = showCounterColumn ? "<th>#</th>" : "";
  const counterCol = showCounterColumn ? '<col class="col-num" />' : "";
  const representativeHeader = showRepresentativeName ? "<th>Representante</th>" : "";
  const representativeCol = showRepresentativeName ? '<col class="col-rep" />' : "";
  const amountHeader = showOrderTotals ? '<th class="amount">Importe pedidos</th>' : "";
  const columnStyles = showRepresentativeName
    ? showOrderTotals
      ? `
    col.col-name { width: 24%; }
    col.col-rep { width: 16%; }
    col.col-cif { width: 10%; }
    col.col-code { width: 11%; }
    col.col-phone { width: 10%; }
    col.col-email { width: 16%; }
    col.col-amount { width: 8%; }
    col.col-sepa { width: 5%; }`
      : `
    col.col-name { width: 27%; }
    col.col-rep { width: 18%; }
    col.col-cif { width: 10%; }
    col.col-code { width: 11%; }
    col.col-phone { width: 11%; }
    col.col-email { width: 17%; }
    col.col-sepa { width: 6%; }`
    : showOrderTotals
      ? `
    col.col-num { width: 4%; }
    col.col-name { width: 27%; }
    col.col-cif { width: 11%; }
    col.col-code { width: 12%; }
    col.col-phone { width: 11%; }
    col.col-email { width: 19%; }
    col.col-amount { width: 10%; }
    col.col-sepa { width: 6%; }`
      : `
    col.col-num { width: 5%; }
    col.col-name { width: 30%; }
    col.col-cif { width: 12%; }
    col.col-code { width: 13%; }
    col.col-phone { width: 12%; }
    col.col-email { width: 22%; }
    col.col-sepa { width: 6%; }`;
  const amountMeta = showOrderTotals
    ? ` &middot; Importe pedidos: <strong>${escapeHtml(formatCurrency(accumulatedOrderTotal))}</strong>`
    : "";
  const amountCol = showOrderTotals ? '<col class="col-amount" />' : "";

  const body = rows
    .map(
      (row, index) => `
        <tr>
          ${showCounterColumn ? `<td class="num">${index + 1}</td>` : ""}
          <td class="text">${escapeHtml(row.name)}</td>
          ${showRepresentativeName ? `<td class="text">${escapeHtml(row.representativeName)}</td>` : ""}
          <td class="mono">${escapeHtml(row.cif)}</td>
          <td class="mono">${escapeHtml(row.code)}</td>
          <td class="text">${escapeHtml(row.phone)}</td>
          <td class="email">${escapeHtml(row.email)}</td>
          ${showOrderTotals ? `<td class="amount">${escapeHtml(formatCurrency(row.orderTotal))}</td>` : ""}
          <td class="center ${row.hasSepa ? "ok" : "ko"}">${row.hasSepa ? "OK" : "KO"}</td>
        </tr>`,
    )
    .join("");

  return `<!DOCTYPE html>
<html lang="es">
<head>
  <meta charset="utf-8" />
  <title>Listado de clientes - SEPA</title>
  <style>
    @page { size: A4 portrait; margin: 8mm; }
    * { box-sizing: border-box; }
    body { font-family: Arial, Helvetica, sans-serif; color: #1f2937; margin: 0; font-size: 9.5px; }
    h1 { font-size: 14px; margin: 0 0 3px; }
    .meta { color: #6b7280; font-size: 9px; margin-bottom: 8px; }
    .meta strong { color: #1f2937; }
    table { width: 100%; border-collapse: collapse; table-layout: fixed; }
    ${columnStyles}
    thead th { background: #f3f4f6; text-align: left; padding: 3px 4px; border-bottom: 1px solid #d1d5db; font-size: 8px; text-transform: uppercase; }
    tbody td { padding: 3px 4px; border-bottom: 1px solid #e5e7eb; line-height: 1.2; vertical-align: top; overflow-wrap: anywhere; }
    tbody tr:nth-child(even) { background: #fafafa; }
    .num { color: #6b7280; }
    .mono { font-family: "Arial Narrow", Arial, Helvetica, sans-serif; }
    .email { overflow-wrap: anywhere; word-break: break-word; }
    .amount { text-align: right; white-space: nowrap; font-variant-numeric: tabular-nums; }
    .center { text-align: center; }
    .ok { color: #047857; font-weight: bold; }
    .ko { color: #b91c1c; font-weight: bold; }
    @media print { body { margin: 0; } }
  </style>
</head>
<body>
  <h1>Listado de clientes - Confirmacion SEPA</h1>
  <div class="meta">
    SEPA: <strong>${escapeHtml(sepaFilterLabel)}</strong> &middot;
    Pedidos: <strong>${escapeHtml(orderFilterLabel)}</strong> &middot;
    Representante: <strong>${escapeHtml(representativeFilterLabel)}</strong> &middot;
    Total: <strong>${rows.length}</strong> &middot;
    Con SEPA: <strong>${withSepa}</strong> &middot;
    Sin SEPA: <strong>${withoutSepa}</strong>${amountMeta} &middot;
    Generado: ${escapeHtml(generatedAt)}
  </div>
  <table>
    <colgroup>
      ${counterCol}
      <col class="col-name" />
      ${representativeCol}
      <col class="col-cif" />
      <col class="col-code" />
      <col class="col-phone" />
      <col class="col-email" />
      ${amountCol}
      <col class="col-sepa" />
    </colgroup>
    <thead>
      <tr>
        ${counterHeader}
        <th>Nombre</th>
        ${representativeHeader}
        <th>CIF</th>
        <th>Codigo</th>
        <th>Telefono</th>
        <th>Email</th>
        ${amountHeader}
        <th class="center">SEPA</th>
      </tr>
    </thead>
    <tbody>${body || `<tr><td colspan="${columnCount}" class="center">Sin resultados</td></tr>`}</tbody>
  </table>
  <script>
    window.onload = function () {
      window.focus();
      window.print();
    };
  </script>
</body>
</html>`;
}

function getOrderFilterLabel(onlyWithOrders, orderSeason) {
  if (!onlyWithOrders) return "Todos los clientes";
  if (orderSeason) return `Con pedido de temporada ${orderSeason}`;
  return "Con algun pedido";
}

function getRepresentativeFilterLabel(representatives, representativeCode) {
  if (!representativeCode) return "Todos";
  const representative = representatives.find((rep) => rep.codRep === representativeCode);
  return representative ? `${representative.name} (${representative.codRep})` : representativeCode;
}

function getCustomerRepresentativeName(representatives, codRep) {
  if (!codRep) return "Sin representante";
  const representative = representatives.find((rep) => rep.codRep === codRep);
  return representative?.name || codRep;
}

export function CustomerSepaList() {
  const { toast } = useToast();

  const [customers, setCustomers] = React.useState([]);
  const [temporadas, setTemporadas] = React.useState([]);
  const [representatives, setRepresentatives] = React.useState([]);
  const [searchTerm, setSearchTerm] = React.useState("");
  const [sepaFilter, setSepaFilter] = React.useState("all");
  const [onlyWithOrders, setOnlyWithOrders] = React.useState(true);
  const [orderSeason, setOrderSeason] = React.useState("");
  const [representativeCode, setRepresentativeCode] = React.useState("");
  const [showOrderTotals, setShowOrderTotals] = React.useState(false);
  const [showRepresentativeName, setShowRepresentativeName] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [seasonsLoading, setSeasonsLoading] = React.useState(false);
  const [representativesLoading, setRepresentativesLoading] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;

    const loadTemporadas = async () => {
      setSeasonsLoading(true);
      const result = await fetchTemporadas();
      if (cancelled) return;
      if (result.error) {
        toast({ title: "Error", description: "No se pudieron cargar las temporadas", variant: "destructive" });
        setTemporadas([]);
      } else {
        setTemporadas(result.data || []);
      }
      setSeasonsLoading(false);
    };

    loadTemporadas();
    return () => {
      cancelled = true;
    };
  }, [toast]);

  React.useEffect(() => {
    let cancelled = false;

    const loadRepresentatives = async () => {
      setRepresentativesLoading(true);
      const result = await fetchCustomerRepresentatives();
      if (cancelled) return;
      if (result.error) {
        toast({ title: "Error", description: "No se pudieron cargar los representantes", variant: "destructive" });
        setRepresentatives([]);
      } else {
        setRepresentatives(result.data || []);
      }
      setRepresentativesLoading(false);
    };

    loadRepresentatives();
    return () => {
      cancelled = true;
    };
  }, [toast]);

  const loadCustomers = React.useCallback(async () => {
    setLoading(true);
    const result = await fetchCustomersSepaStatus({
      searchTerm,
      sepaFilter,
      onlyWithOrders,
      orderSeason: onlyWithOrders ? orderSeason : "",
      includeOrderTotals: showOrderTotals,
      representativeCode,
    });
    if (result.error) {
      toast({ title: "Error", description: "No se pudieron cargar los clientes", variant: "destructive" });
      setCustomers([]);
    } else {
      setCustomers(result.data || []);
    }
    setLoading(false);
  }, [searchTerm, sepaFilter, onlyWithOrders, orderSeason, showOrderTotals, representativeCode, toast]);

  React.useEffect(() => {
    const timeout = setTimeout(loadCustomers, 250);
    return () => clearTimeout(timeout);
  }, [loadCustomers]);

  const listedCustomers = React.useMemo(() => {
    const rows = customers.map((customer) => ({
      ...customer,
      representativeName: getCustomerRepresentativeName(representatives, customer.codRep),
    }));

    if (!showRepresentativeName) return rows;

    return [...rows].sort((a, b) => {
      const representativeCompare = a.representativeName.localeCompare(b.representativeName, "es");
      if (representativeCompare) return representativeCompare;
      const codeCompare = String(a.codRep || "").localeCompare(String(b.codRep || ""), "es");
      if (codeCompare) return codeCompare;
      return a.name.localeCompare(b.name, "es");
    });
  }, [customers, representatives, showRepresentativeName]);

  const withSepa = listedCustomers.filter((c) => c.hasSepa).length;
  const withoutSepa = listedCustomers.length - withSepa;
  const accumulatedOrderTotal = listedCustomers.reduce((sum, customer) => sum + (Number(customer.orderTotal) || 0), 0);
  const orderFilterLabel = getOrderFilterLabel(onlyWithOrders, orderSeason);
  const representativeFilterLabel = getRepresentativeFilterLabel(representatives, representativeCode);
  const showCounterColumn = !showRepresentativeName;
  const tableColumnCount =
    (showCounterColumn ? 1 : 0) + 5 + (showRepresentativeName ? 1 : 0) + (showOrderTotals ? 1 : 0) + 1;

  const handleOnlyWithOrdersChange = (checked) => {
    setOnlyWithOrders(checked);
    if (!checked) setOrderSeason("");
  };

  const handlePrint = () => {
    if (listedCustomers.length === 0) {
      toast({ title: "Sin datos", description: "No hay clientes para imprimir", variant: "destructive" });
      return;
    }
    const sepaFilterLabel = sepaOptions.find((o) => o.value === sepaFilter)?.label || "Todos";
    const html = buildPrintHtml(listedCustomers, {
      sepaFilterLabel,
      orderFilterLabel,
      representativeFilterLabel,
      showOrderTotals,
      showRepresentativeName,
    });
    const printWindow = window.open("", "_blank", "width=900,height=700");
    if (!printWindow) {
      toast({
        title: "Bloqueado",
        description: "Permite las ventanas emergentes para generar el PDF",
        variant: "destructive",
      });
      return;
    }
    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
  };

  const handleDownloadCsv = () => {
    if (listedCustomers.length === 0) {
      toast({ title: "Sin datos", description: "No hay clientes para exportar", variant: "destructive" });
      return;
    }
    const csv = buildCsv(listedCustomers, { showOrderTotals, showRepresentativeName });
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    const stamp = new Date().toISOString().slice(0, 10);
    const seasonSuffix = onlyWithOrders && orderSeason ? `-temporada-${filePart(orderSeason)}` : "";
    const representativeSuffix = representativeCode ? `-rep-${filePart(representativeCode)}` : "";
    link.href = url;
    link.download = `clientes-sepa${seasonSuffix}${representativeSuffix}-${stamp}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-xl">
            <Users className="h-5 w-5" />
            Listado de clientes (SEPA)
          </CardTitle>
          <CardDescription>
            Lista todos los clientes indicando si tienen la firma SEPA informada. Filtra por estado, busca por nombre,
            CIF, codigo, telefono, email o representante, e imprime o guarda como PDF.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-3 xl:grid-cols-[minmax(0,1fr)_190px_300px_240px_230px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <Input
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Buscar por nombre, CIF, codigo, telefono o email..."
                className="pl-9"
              />
            </div>
            <select
              value={sepaFilter}
              onChange={(event) => setSepaFilter(event.target.value)}
              className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
            >
              {sepaOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>

            <div className="grid gap-2 sm:grid-cols-[auto_minmax(0,1fr)] sm:items-center">
              <label className="flex w-fit items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={onlyWithOrders}
                  onChange={(event) => handleOnlyWithOrdersChange(event.target.checked)}
                  className="h-4 w-4"
                />
                Solo clientes con algun pedido
              </label>
              <SeasonSelect data-season-selector="true"
                value={orderSeason}
                onChange={(event) => setOrderSeason(event.target.value)}
                disabled={!onlyWithOrders || seasonsLoading}
                className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm disabled:cursor-not-allowed disabled:opacity-50"
              >
                <option value="">{seasonsLoading ? "Cargando temporadas..." : "Todas las temporadas"}</option>
                {temporadas.map((temporada) => (
                  <option key={temporada} value={temporada}>
                    {temporada}
                  </option>
                ))}
              </SeasonSelect>
            </div>

            <select
              value={representativeCode}
              onChange={(event) => setRepresentativeCode(event.target.value)}
              disabled={representativesLoading}
              className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm disabled:cursor-not-allowed disabled:opacity-50"
            >
              <option value="">
                {representativesLoading ? "Cargando representantes..." : "Todos los representantes"}
              </option>
              {representatives.map((representative) => (
                <option key={representative.codRep} value={representative.codRep}>
                  {representative.name} ({representative.codRep})
                </option>
              ))}
            </select>

            <div className="grid gap-2 text-sm text-gray-700">
              <label className="flex w-fit items-center gap-2">
                <input
                  type="checkbox"
                  checked={showOrderTotals}
                  onChange={(event) => setShowOrderTotals(event.target.checked)}
                  className="h-4 w-4"
                />
                Mostrar importe pedidos
              </label>
              <label className="flex w-fit items-center gap-2">
                <input
                  type="checkbox"
                  checked={showRepresentativeName}
                  onChange={(event) => setShowRepresentativeName(event.target.checked)}
                  className="h-4 w-4"
                />
                Mostrar representante
              </label>
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex flex-wrap items-center gap-2 text-sm text-gray-600">
              <Badge variant="secondary">{listedCustomers.length} clientes</Badge>
              <Badge className="bg-emerald-600 hover:bg-emerald-600">{withSepa} con SEPA</Badge>
              <Badge variant="destructive">{withoutSepa} sin SEPA</Badge>
              {onlyWithOrders && <Badge variant="outline">{orderFilterLabel}</Badge>}
              {representativeCode && <Badge variant="outline">Rep. {representativeFilterLabel}</Badge>}
              {showRepresentativeName && <Badge variant="outline">Ordenado por representante</Badge>}
              {showOrderTotals && <Badge variant="outline">Pedidos {formatCurrency(accumulatedOrderTotal)}</Badge>}
              <Button type="button" variant="ghost" size="sm" onClick={loadCustomers} disabled={loading}>
                <RefreshCw className={`mr-1 h-4 w-4 ${loading ? "animate-spin" : ""}`} />
                Refrescar
              </Button>
            </div>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={handleDownloadCsv}
                disabled={loading || listedCustomers.length === 0}
              >
                <Download className="mr-2 h-4 w-4" />
                Descargar CSV
              </Button>
              <Button type="button" onClick={handlePrint} disabled={loading || listedCustomers.length === 0}>
                <Printer className="mr-2 h-4 w-4" />
                Imprimir / PDF
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <div className="max-h-[60vh] overflow-auto">
            <table className="w-full table-fixed text-xs">
              <thead className="sticky top-0 bg-gray-100 text-left text-xs uppercase text-gray-600">
                <tr>
                  {showCounterColumn && <th className="w-12 px-2 py-2">#</th>}
                  <th className={`${showRepresentativeName ? "w-[24%]" : showOrderTotals ? "w-[27%]" : "w-[30%]"} px-2 py-2`}>
                    Nombre
                  </th>
                  {showRepresentativeName && <th className="w-[16%] px-2 py-2">Representante</th>}
                  <th className={`${showRepresentativeName ? "w-[10%]" : showOrderTotals ? "w-[11%]" : "w-[12%]"} px-2 py-2`}>CIF</th>
                  <th className={`${showRepresentativeName ? "w-[11%]" : showOrderTotals ? "w-[12%]" : "w-[13%]"} px-2 py-2`}>Codigo</th>
                  <th className={`${showRepresentativeName ? "w-[10%]" : showOrderTotals ? "w-[11%]" : "w-[12%]"} px-2 py-2`}>Telefono</th>
                  <th className={`${showRepresentativeName ? "w-[16%]" : showOrderTotals ? "w-[19%]" : "w-[22%]"} px-2 py-2`}>Email</th>
                  {showOrderTotals && <th className="w-24 px-2 py-2 text-right">Importe pedidos</th>}
                  <th className="w-16 px-2 py-2 text-center">SEPA</th>
                </tr>
              </thead>
              <tbody>
                {listedCustomers.length === 0 && !loading && (
                  <tr>
                    <td colSpan={tableColumnCount} className="px-3 py-8 text-center text-gray-500">
                      No hay clientes con los filtros actuales.
                    </td>
                  </tr>
                )}
                {listedCustomers.map((customer, index) => (
                  <tr key={customer.id} className="border-t hover:bg-gray-50">
                    {showCounterColumn && <td className="px-2 py-2 text-gray-500">{index + 1}</td>}
                    <td className="break-words px-2 py-2">{customer.name || "-"}</td>
                    {showRepresentativeName && (
                      <td className="break-words px-2 py-2">{customer.representativeName || "-"}</td>
                    )}
                    <td className="break-words px-2 py-2 font-mono">{customer.cif || "-"}</td>
                    <td className="break-words px-2 py-2 font-mono">{customer.code || "-"}</td>
                    <td className="break-words px-2 py-2">{customer.phone || "-"}</td>
                    <td className="break-all px-2 py-2">{customer.email || "-"}</td>
                    {showOrderTotals && (
                      <td className="whitespace-nowrap px-2 py-2 text-right font-mono">
                        {formatCurrency(customer.orderTotal)}
                      </td>
                    )}
                    <td className="px-2 py-2 text-center">
                      {customer.hasSepa ? (
                        <Badge className="bg-emerald-600 hover:bg-emerald-600">OK</Badge>
                      ) : (
                        <Badge variant="destructive">KO</Badge>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
